import { useEffect, useRef, useState } from 'react';

import { fetchItemsByIds } from '../services/itemsBulk';

const rowId = row => {
	const raw = row?._id;
	return typeof raw === 'string' ? raw : (raw?.$oid || '');
};

/**
 * The records a card lookup's SELECTED value points at that the picker's own
 * fetch did not bring back — outside its first page (a published Medications
 * collection holds 1,102 products and the picker lists the newest 50), in a
 * collection other than the form's, or no longer readable — each asked for
 * ONCE, by id (`GET /api/items?ids=`), so the value reads as the record's
 * title and not as its id (KPR-1233).
 *
 *   const { rows, settled } = useSelectedLookupRows({ ids, isKnown, enabled });
 *     rows     { id: row } for the ids that were asked and came back
 *     settled  the ids that were asked and have an answer (a row, or the
 *              server's "not readable" — which the not-readable registry
 *              also carries, for the chip that says so)
 *
 * `isKnown(id)` says the caller already holds the record. `enabled` is false
 * until the caller's own fetch has settled, so an id its page carries is not
 * asked for twice.
 *
 * An answer is kept whenever it arrives. The ask is made once per id, so
 * an answer dropped because a dependency moved on (the picker re-fetching from
 * the card's own collection once the card is known — always, for a global
 * card) would leave the id unresolved for good: the raw id, for ever. Only
 * unmounting discards one. A failed ask is forgotten, so the next change
 * retries it.
 */
export default function useSelectedLookupRows({ ids, isKnown, enabled = true }) {
	const [state, setState] = useState({ rows: {}, settled: {} });
	const askedRef = useRef(new Set());
	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => { mountedRef.current = false; };
	}, []);

	const key = (ids || []).join(',');
	useEffect(() => {
		if (!enabled || !key) return;
		const missing = key.split(',').filter(id => id && !isKnown(id) && !askedRef.current.has(id));
		if (!missing.length) return;
		missing.forEach(id => askedRef.current.add(id));
		fetchItemsByIds(missing)
			.then(found => {
				if (!mountedRef.current) return;
				setState(prev => {
					const rows = { ...prev.rows };
					const settled = { ...prev.settled };
					found.forEach(row => { const id = rowId(row); if (id) rows[id] = row; });
					missing.forEach(id => { settled[id] = true; });
					return { rows, settled };
				});
			})
			.catch(() => { missing.forEach(id => askedRef.current.delete(id)); });
	}, [enabled, key, isKnown]);

	return state;
}
